import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Button from '@ui-elements/Button';
import InputWithLabel from '@ui-parts/InputWithLabel';
import { useForm, Controller } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MessageArea from '@ui-parts/MessageArea';
import { PALETTE } from '~/assets/styles/base';
import { initialize } from '~/features/passwordReset/passwordResetSlice';
import { updateAdminPassword, callThunk } from '~/api';
import { ROUTES } from '~/constants/routes';
import { passwordValidationRule } from '~/constants/validationRules';

const PasswordResetInputBox = styled.div`
  max-width: 720px;
  min-height: 472px;
  border: 1px solid ${PALETTE.border.frame};
  margin: 58px auto 0;
  background: ${PALETTE.white};
`;

const StyledForm = styled.form`
  display: block;
  margin: 0 120px;
`;

const ButtonBox = styled.div`
  padding: 35px;
  text-align: center;
`;

function AdminPasswordResetInput() {
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get('token');

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const passwordReset = useSelector((state) => state.passwordReset);
  const navigate = useNavigate();

  const { control, handleSubmit, getValues } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      newPassword: '',
      confirmNewPassword: '',
    },
  });

  const onSubmit = (data) => {
    dispatch(
      callThunk({
        action: updateAdminPassword,
        payload: {
          password: data.newPassword,
          token,
        },
      })
    );
  };

  useEffect(() => {
    dispatch(initialize());
  }, []);

  useEffect(() => {
    if (passwordReset.isReset) {
      navigate(ROUTES.ADMIN.PASSWORD_RESET_COMPLETED);
    }
  }, [passwordReset.isReset]);

  return (
    <PasswordResetInputBox>
      <MessageArea
        message={t('passwordResetMailInput.title')}
        desc={t('passwordResetMailInput.desc')}
      />
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="newPassword"
          control={control}
          rules={passwordValidationRule}
          render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
            <InputWithLabel
              type="password"
              label={t('passwordResetMailInput.newPassword')}
              placeholder=""
              name={name}
              value={value}
              handleChange={(e) => {
                onChange(e);
              }}
              handleBlur={(e) => {
                onBlur(e);
              }}
              error={Boolean(error)}
              errorMessage={error?.message}
              dataTestId="pages-reset-input-password"
              width="453px"
              description={t('passwordResetMailInput.passwordNote')}
              background={PALETTE.bg.main}
              fontWeight="bold"
              fontSize="note2"
              padding="15px 10px"
              autoComplete="off"
              inputProps={{ maxLength: 24 }}
              ref={ref}
            />
          )}
        />
        <Controller
          name="confirmNewPassword"
          control={control}
          rules={{
            ...passwordValidationRule,
            validate: {
              message: (v) => (v === getValues('newPassword') ? null : t('validation.pswNotSame')),
            },
          }}
          render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
            <InputWithLabel
              type="password"
              label={t('passwordResetMailInput.confirmNewPassword')}
              placeholder=""
              name={name}
              value={value}
              handleChange={(e) => {
                onChange(e);
              }}
              handleBlur={(e) => {
                onBlur(e);
              }}
              error={Boolean(error)}
              errorMessage={error?.message}
              dataTestId="pages-reset-input-confirmpassword"
              width="453px"
              fontWeight="bold"
              autoComplete="off"
              inputProps={{ maxLength: 24 }}
              ref={ref}
            />
          )}
        />

        <ButtonBox>
          <Button size="medium" color="primary" variant="contained" type="submit">
            {t('appCommon.submit')}
          </Button>
        </ButtonBox>
      </StyledForm>
    </PasswordResetInputBox>
  );
}

export default AdminPasswordResetInput;
